import React from 'react'
import PropTypes from 'prop-types'
import Card from 'react-bootstrap/Card'
import Table from 'react-bootstrap/Table'
import Badge from 'react-bootstrap/Badge'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import { formatDateSlash } from 'helpers/datetime'
import TableError from 'shared/tables/table-error'
import MutedText from 'shared/forms/muted-text'
import { HighRiskPermissionBadge, InternalConflict, ConflictBadge } from 'shared/badges'

// r.status is a string, so a truthiness check rendered every role as Active.
const ROLE_STATUS_STYLES = {
  Active: 'bg-light text-success mx-1',
  Inactive: 'bg-light text-info mx-1',
  Deleted: 'bg-light text-danger mx-1'
}

const RoleStatusBadge = ({ status }) => (
  <Badge pill className={ROLE_STATUS_STYLES[status]} variant="light">
    {status}
  </Badge>
)

RoleStatusBadge.propTypes = { status: PropTypes.string }

function RolesTab({ employee, isError }) {
  return (
    <Card className="border">
      <Card.Header className="bg-white">
        <div className="text-info">Roles</div>
      </Card.Header>
      <Card.Body>
        <Table responsive size="sm">
          <thead>
            <tr>
              <th colSpan={4}>Role</th>
              <th colSpan={2} className="text-center">
                Conflicts
              </th>
              <th colSpan={3} className="text-center">
                Permissions
              </th>
            </tr>
            <tr>
              <th>Name</th>
              <th>
                <OverlayTrigger
                  placement="top"
                  delay={{ show: 250, hide: 400 }}
                  overlay={
                    <Tooltip>
                      Indicates whether the role is active in the system, not
                      whether the employee currently has this role assigned.
                    </Tooltip>
                  }
                >
                  <span style={{ cursor: 'pointer' }}>
                    Role Status
                    <FontAwesomeIcon icon={faInfoCircle} className="ms-1" />
                  </span>
                </OverlayTrigger>
              </th>
              <th>Start</th>
              <th>End</th>
              <th className="border-start ps-4">Internal to Role</th>
              <th className="border-end">Creating Conflict?</th>
              <th>High Risk</th>
              <th>Super User</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {employee.roles.map((r) => (
              <tr key={`role-row-${r.id}`}>
                <td>
                  <a href={`/roles/${r.id}`} target="_blank" rel="noreferrer">
                    {r.name}
                  </a>
                  <MutedText as="div">{r.caption}</MutedText>
                </td>
                <td>
                  <RoleStatusBadge status={r.status} />
                </td>
                <td className="text-muted">{formatDateSlash(r.assigned_on)}</td>
                <td className="text-muted">{formatDateSlash(r.removed_on)}</td>

                <td className="text-center border-start">
                  {r.internal_conflicts_count > 0 && (
                    <InternalConflict
                      count={r.internal_conflicts_count}
                      textDisplay="Internal"
                    />
                  )}
                </td>

                <td className="text-center">
                  {employee.role_ids_causing_conflicts.includes(r.id) && (
                    <ConflictBadge text=" Yes" />
                  )}
                </td>

                <td className="border-start text-center">
                  {r.high_risk_permissions_count > 0 && (
                    <HighRiskPermissionBadge
                      count={r.high_risk_permissions_count}
                    />
                  )}
                </td>
                <td className="text-muted text-center">
                  {r.superuser_permissions_count}
                </td>
                <td className="text-muted text-center">
                  {r.permissions_count}
                </td>
              </tr>
            ))}
            {isError && <TableError columns={7} />}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  )
}

export default RolesTab
